import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Campaign {
  id: string
  name: string
  type: string
  status: string
  budget: number
  spent: number
  impressions: number
  clicks: number
  conversions: number
  ctr: number
  cvr: number
  cpa: number
  roas: number
}

interface ChannelBreakdown {
  id: string
  channel: string
  campaigns: number
  budget: number
  spent: number
  revenue: number
  roas: number
  contribution: number
}

interface Statistics {
  total_campaigns: number
  active_campaigns: number
  total_budget: number
  total_spent: number
  total_revenue: number
  avg_roas: number
}

export default function CampaignAnalyticsPage() {
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [channels, setChannels] = useState<ChannelBreakdown[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [campaignsRes, channelsRes, statsRes] = await Promise.all([
        fetch('/api/campaignanalytics/campaigns'),
        fetch('/api/campaignanalytics/channels'),
        fetch('/api/campaignanalytics/statistics')
      ])
      if (campaignsRes.ok) setCampaigns(await campaignsRes.json())
      if (channelsRes.ok) setChannels(await channelsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching campaign analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value)
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getStatusBadge = (status: string) => {
    if (status === 'active') return 'bg-green-100 text-green-800'
    if (status === 'completed') return 'bg-blue-100 text-blue-800'
    if (status === 'scheduled') return 'bg-yellow-100 text-yellow-800'
    return 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Campaign Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 241 - Campaign performance and channel analytics</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Campaigns</p>
              <p className="text-2xl font-bold text-gray-900">{statistics.total_campaigns}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Active</p>
              <p className="text-2xl font-bold text-green-600">{statistics.active_campaigns}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Budget</p>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(statistics.total_budget)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Spent</p>
              <p className="text-2xl font-bold text-orange-600">{formatCurrency(statistics.total_spent)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Revenue</p>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(statistics.total_revenue)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Avg ROAS</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_roas}x</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Campaigns</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Spent</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impressions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">CTR</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ROAS</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id}>
                    <td className="px-6 py-4 font-medium text-gray-900">{campaign.name}</td>
                    <td className="px-6 py-4 text-gray-500">{campaign.type}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(campaign.status)}`}>{campaign.status}</span>
                    </td>
                    <td className="px-6 py-4 text-blue-600">{formatCurrency(campaign.spent)}</td>
                    <td className="px-6 py-4 text-gray-500">{formatNumber(campaign.impressions)}</td>
                    <td className="px-6 py-4 text-gray-500">{campaign.ctr}%</td>
                    <td className="px-6 py-4 text-green-600 font-medium">{formatNumber(campaign.conversions)}</td>
                    <td className="px-6 py-4 text-purple-600 font-bold">{campaign.roas}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Channel Performance</h2>
          </div>
          <div className="p-6 space-y-4">
            {channels.map((channel) => (
              <div key={channel.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-900">{channel.channel}</span>
                  <span className="text-lg font-bold text-purple-600">{channel.roas}x ROAS</span>
                </div>
                <div className="h-2 bg-gray-200 rounded-full mb-2">
                  <div className="h-2 bg-blue-500 rounded-full" style={{width: `${channel.contribution}%`}}></div>
                </div>
                <div className="grid grid-cols-4 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Campaigns</p>
                    <p className="font-medium">{channel.campaigns}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Budget</p>
                    <p className="font-medium">{formatCurrency(channel.budget)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Revenue</p>
                    <p className="font-medium text-green-600">{formatCurrency(channel.revenue)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Contribution</p>
                    <p className="font-medium text-blue-600">{channel.contribution}%</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
